/**
 * Godown Allocation Approval Page
 * ─────────────────────────────────────────────────────────────────────────────
 * Store Manager's screen. Grouped by Mill Lot.
 *
 * Workflow:
 *   Worker assigns godown (GodownAssignmentPage) → status = PENDING_APPROVAL
 *   Store Manager physically walks to the bay, verifies each lot's placement,
 *   clicks "Approve Lot" → all bales in that lot move to APPROVED.
 */
import { useState } from 'react'
import {
  App, Badge, Button, Card, Col, Descriptions, Drawer, Empty,
  Popconfirm, Row, Space, Statistic, Table, Tag, Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  CheckCircleOutlined, ClockCircleOutlined, EnvironmentOutlined,
  ReloadOutlined,
} from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import apiClient from '@/shared/api/client'
import { usePlantId } from '@/shared/hooks/usePlantId'
import PageHeader from '@/shared/components/PageHeader'

const { Text } = Typography

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────
interface PendingBale {
  id: string
  bale_no: string
  grn_no: string
  lot_no: string | null
  mill_lot_id: string | null
  mill_lot_no: string | null
  cotton_variety: string | null
  crop_year: string | null
  weight_kg: string
  godown: string | null
  row_no: string | null
  col_no: string | null
  level: string | null
  location: string
  godown_allocation_status: string | null
  supplier_name: string
  created_at: string
}

interface LotGroup {
  mill_lot_id: string | null
  mill_lot_no: string | null
  cotton_variety: string | null
  crop_year: string | null
  supplier_name: string
  godown: string | null
  location_summary: string
  bales: PendingBale[]
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────────────────────
export default function GodownApprovalPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const { plantId } = usePlantId()
  const [selectedBale, setSelectedBale] = useState<PendingBale | null>(null)
  const [approvingLot, setApprovingLot] = useState<string | null>(null)

  // Fetch all bales pending godown approval
  const { data: pending = [], isLoading, refetch } = useQuery<PendingBale[]>({
    queryKey: ['godown-pending', plantId],
    queryFn: () =>
      apiClient
        .get('/bales/pending-godown-approval', { params: { plant_id: plantId } })
        .then(r => r.data),
    enabled: !!plantId,
    refetchInterval: 30_000,
  })

  // Group bales by Mill Lot
  const lotGroups: LotGroup[] = (() => {
    const map = new Map<string, LotGroup>()
    for (const bale of pending) {
      const key = bale.mill_lot_id ?? `grn_${bale.grn_no}`
      if (!map.has(key)) {
        const parts = [bale.godown, bale.row_no, bale.col_no, bale.level].filter(Boolean)
        map.set(key, {
          mill_lot_id: bale.mill_lot_id,
          mill_lot_no: bale.mill_lot_no ?? `GRN: ${bale.grn_no}`,
          cotton_variety: bale.cotton_variety,
          crop_year: bale.crop_year,
          supplier_name: bale.supplier_name,
          godown: bale.godown,
          location_summary: parts.join('-') || '—',
          bales: [],
        })
      }
      map.get(key)!.bales.push(bale)
    }
    return Array.from(map.values())
  })()

  // Helper — invalidate BOTH lists so the partner page (Assign Godown)
  // doesn't show stale "Pending Approval" badges after the manager approves here.
  const invalidateGodownLists = () => {
    qc.invalidateQueries({ queryKey: ['godown-pending', plantId] })
    qc.invalidateQueries({ queryKey: ['mill-lot-godown-summary', plantId] })
  }

  // Approve single bale
  const approveBale = useMutation({
    mutationFn: (baleId: string) =>
      apiClient.post(`/bales/${baleId}/approve-godown`).then(r => r.data),
    onSuccess: () => {
      message.success('Bale approved ✓')
      invalidateGodownLists()
      setSelectedBale(null)
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  // Approve entire Mill Lot
  const approveLot = useMutation({
    mutationFn: (mill_lot_id: string) =>
      apiClient
        .post('/bales/approve-godown-by-lot', null, { params: { mill_lot_id } })
        .then(r => r.data),
    onSuccess: (data) => {
      message.success(`✓ ${data.approved} bales approved for this Mill Lot`)
      invalidateGodownLists()
      setApprovingLot(null)
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Approval failed'),
  })

  const baleColumns: ColumnsType<PendingBale> = [
    {
      title: 'Bale No',
      dataIndex: 'bale_no',
      key: 'bale_no',
      width: 130,
      render: (v: string) => <Text code style={{ fontSize: 11 }}>{v}</Text>,
    },
    {
      title: 'GRN',
      dataIndex: 'grn_no',
      key: 'grn',
      width: 110,
      render: (v: string) => <Text style={{ fontSize: 11 }}>{v}</Text>,
    },
    {
      title: 'Weight (kg)',
      dataIndex: 'weight_kg',
      key: 'wt',
      width: 90,
      align: 'right',
      render: (v: any) => Number(v || 0).toFixed(1),
    },
    {
      title: 'Location',
      key: 'loc',
      width: 160,
      render: (_: unknown, r: PendingBale) => (
        <Space direction="vertical" size={1}>
          <Tag icon={<EnvironmentOutlined />} color="blue" style={{ fontSize: 11 }}>
            {r.godown}
          </Tag>
          {(r.row_no || r.col_no || r.level) && (
            <Text type="secondary" style={{ fontSize: 10 }}>
              {[r.row_no, r.col_no, r.level].filter(Boolean).join(' / ')}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Received',
      dataIndex: 'created_at',
      key: 'dt',
      width: 100,
      render: (v: string) => (
        <Text type="secondary" style={{ fontSize: 10 }}>
          {v ? dayjs(v).format('DD-MMM HH:mm') : '—'}
        </Text>
      ),
    },
    {
      title: '',
      key: 'act',
      width: 100,
      render: (_: unknown, r: PendingBale) => (
        <Popconfirm
          title={`Confirm bale ${r.bale_no} placed in ${r.godown}?`}
          onConfirm={() => approveBale.mutate(r.id)}
          okText="Approve"
        >
          <Button size="small" type="link" icon={<CheckCircleOutlined />}>
            Approve
          </Button>
        </Popconfirm>
      ),
    },
  ]

  const totalWeight = pending.reduce((s, b) => s + Number(b.weight_kg || 0), 0)

  return (
    <div>
      <PageHeader
        title="Godown Approval"
        subtitle="Store Manager confirms physical bale placement — grouped by Mill Lot"
        extra={
          <Button icon={<ReloadOutlined />} onClick={() => refetch()}>Refresh</Button>
        }
      />

      {/* KPI strip */}
      <Row gutter={12} style={{ marginBottom: 16 }}>
        <Col xs={8} sm={6}>
          <Card size="small" style={{ borderTop: '3px solid #faad14' }}>
            <Statistic
              title={<span style={{ fontSize: 11 }}>Pending Bales</span>}
              value={pending.length}
              valueStyle={{ color: '#faad14', fontWeight: 700 }}
              prefix={<ClockCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={8} sm={6}>
          <Card size="small" style={{ borderTop: '3px solid #1677ff' }}>
            <Statistic
              title={<span style={{ fontSize: 11 }}>Mill Lots</span>}
              value={lotGroups.length}
              valueStyle={{ color: '#1677ff', fontWeight: 700 }}
            />
          </Card>
        </Col>
        <Col xs={8} sm={6}>
          <Card size="small" style={{ borderTop: '3px solid #52c41a' }}>
            <Statistic
              title={<span style={{ fontSize: 11 }}>Weight (kg)</span>}
              value={totalWeight.toFixed(0)}
              valueStyle={{ color: '#52c41a', fontWeight: 700 }}
            />
          </Card>
        </Col>
      </Row>

      {/* Grouped by Mill Lot */}
      {!plantId ? (
        <Empty description="Select a plant" />
      ) : isLoading ? (
        <Card loading />
      ) : lotGroups.length === 0 ? (
        <Card>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Space direction="vertical" size={4} style={{ alignItems: 'center' }}>
                <span>
                  <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 6 }} />
                  No pending godown approvals
                </span>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Lots only appear here once the Store Worker has assigned them a location
                  on the <b>Godown Assignment</b> page. If you expect lots here, check that
                  page first.
                </Text>
              </Space>
            }
          />
        </Card>
      ) : (
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          {lotGroups.map(lot => (
            <Card
              key={lot.mill_lot_id ?? lot.mill_lot_no}
              size="small"
              title={
                <Space wrap>
                  <Badge count={lot.bales.length} color="#faad14" />
                  <Text code style={{ fontSize: 13 }}>{lot.mill_lot_no}</Text>
                  {lot.cotton_variety && (
                    <Tag color="blue" style={{ fontSize: 11 }}>
                      {lot.cotton_variety}{lot.crop_year ? ` (${lot.crop_year})` : ''}
                    </Tag>
                  )}
                  <Tag icon={<EnvironmentOutlined />} color="geekblue" style={{ fontSize: 11 }}>
                    {lot.location_summary}
                  </Tag>
                </Space>
              }
              extra={
                <Popconfirm
                  title={`Approve all ${lot.bales.length} bales in ${lot.mill_lot_no}?`}
                  description={
                    <span>
                      Confirm physical placement in <b>{lot.godown}</b> for all bales in this lot.
                    </span>
                  }
                  onConfirm={() => {
                    if (!lot.mill_lot_id) {
                      // Fall back to per-bale approval when no mill_lot_id
                      lot.bales.forEach(b => approveBale.mutate(b.id))
                    } else {
                      setApprovingLot(lot.mill_lot_id)
                      approveLot.mutate(lot.mill_lot_id)
                    }
                  }}
                  okText="Approve All"
                >
                  <Button
                    type="primary"
                    size="small"
                    icon={<CheckCircleOutlined />}
                    loading={approvingLot === lot.mill_lot_id && approveLot.isPending}
                  >
                    Approve Lot ({lot.bales.length})
                  </Button>
                </Popconfirm>
              }
              style={{ borderLeft: '4px solid #faad14' }}
            >
              <Table
                rowKey="id"
                size="small"
                dataSource={lot.bales}
                columns={baleColumns}
                pagination={false}
                scroll={{ x: 700 }}
              />
            </Card>
          ))}
        </Space>
      )}

      {/* Single bale detail drawer */}
      <Drawer
        title={`Bale ${selectedBale?.bale_no}`}
        open={!!selectedBale}
        onClose={() => setSelectedBale(null)}
        width={380}
        footer={
          selectedBale && (
            <Popconfirm
              title="Confirm physical placement?"
              onConfirm={() => approveBale.mutate(selectedBale.id)}
            >
              <Button type="primary" block icon={<CheckCircleOutlined />} loading={approveBale.isPending}>
                Approve Placement
              </Button>
            </Popconfirm>
          )
        }
      >
        {selectedBale && (
          <Descriptions bordered size="small" column={1}>
            <Descriptions.Item label="Bale No"><Text code>{selectedBale.bale_no}</Text></Descriptions.Item>
            <Descriptions.Item label="Mill Lot">{selectedBale.mill_lot_no || '—'}</Descriptions.Item>
            <Descriptions.Item label="Variety">
              {selectedBale.cotton_variety || '—'}{selectedBale.crop_year ? ` (${selectedBale.crop_year})` : ''}
            </Descriptions.Item>
            <Descriptions.Item label="Weight">{Number(selectedBale.weight_kg).toFixed(3)} kg</Descriptions.Item>
            <Descriptions.Item label="Godown">
              <Tag color="blue">{selectedBale.godown}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Row / Col / Level">
              {[selectedBale.row_no, selectedBale.col_no, selectedBale.level].filter(Boolean).join(' / ') || '—'}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Drawer>
    </div>
  )
}
